import * as logClient from "@classdojo/log-client";
import { useWatch } from "@web-monorepo/hooks";
import { useCallback, useMemo, useRef, useState } from "react";
import { ZendeskAPI } from "react-zendesk";
import { getSessionData } from "./useSession";
import useDecagonAuthTokenMutation from "./api/auth/useDecagonAuthTokenMutation";
import { metrics } from "#/src/utils/metrics";
import { subHours } from "#/src/utils/dates";
import { isProduction } from "#/src/utils/env";
import { IS_TEST_SERVER } from "#/src/constants/client";

function getDecagonApiHost(): string | null {
  if (IS_TEST_SERVER) return "tutor-test.classdojo.com";
  if (isProduction) return "tutor.classdojo.com";

  return Config.decagonApiHost;
}

function getDecagonToolBranch(): string | undefined {
  if (isProduction && !IS_TEST_SERVER) return undefined;

  return Config.decagonToolBranch ?? "main";
}

type ZendeskWidgetFields = {
  email?: string;
  name?: string;
};

const ticketIdByUserType = {
  parent: 24658308388109,
  teacher: 24314991618061,
};

export type WindowWithHelperAPIMethods = Window &
  typeof globalThis & {
    duet?: {
      setUserId: (userId: string) => void;
      setMetadata: (metadata: Record<string, unknown>) => void;
      open: () => void;
      unhide: () => void;
      hide: () => void;
      setEventListener: (callback: (event: { eventName: string }) => void) => void;
      setUserIdAuth: (signature: string, epoch: number) => void;
    };
  };

export const DECAGON_LOADER_SRC = "https://decagon.ai/loaders/classdojo.js";

export const getDecagonLoaderScript = () =>
  document.querySelector<HTMLScriptElement>(`script[src="${DECAGON_LOADER_SRC}"]`);

const isDecagonLoaderInjected = () => getDecagonLoaderScript() != null;

const DUET_POLL_INTERVAL_MS = 100;
// A blocked loader (ad blocker, network failure) is injected but never defines `duet`; stop polling
// for it after this long so each call doesn't leave an interval running forever.
export const DUET_POLL_TIMEOUT_MS = 30_000;

const windowWithDecagon = window as WindowWithHelperAPIMethods;
export const callDecagonMethod = (callback: () => void) => {
  // silent the error since we don't want to break the app if the method is not available
  // this error handling is only for the decagon widget
  try {
    if (windowWithDecagon.duet == null) {
      // The loader is only injected for Decagon users (see HelperWidgetProvider). Without it `duet`
      // never appears, so don't start a poll that would never clear.
      if (!isDecagonLoaderInjected()) return;

      let attemptsLeft = DUET_POLL_TIMEOUT_MS / DUET_POLL_INTERVAL_MS;
      const interval = setInterval(() => {
        attemptsLeft -= 1;
        if (windowWithDecagon.duet != null) {
          clearInterval(interval);
          callback();
        } else if (attemptsLeft <= 0) {
          clearInterval(interval);
        }
      }, DUET_POLL_INTERVAL_MS);
    } else {
      callback();
    }
    // eslint-disable-next-line no-catch-all/no-catch-all -- helper widget callback may throw arbitrary runtime errors; we want to swallow + report them all rather than break the host page
  } catch (e) {
    logClient.logMessage("ERROR", "marketplace.helperWidgetCallbackFailed", {
      site: "marketplace",
      errorName: e instanceof Error ? e.name : "unknown",
      errorMessage: e instanceof Error ? e.message : String(e),
    });
  }
};

export type WidgetUserData = {
  userId: string;
  userType: "parent" | "teacher";
  email: string;
  fullname: string;
  caller: string;
};

export const hideDecagonWidget = () => callDecagonMethod(() => windowWithDecagon.duet?.hide());

const useHelperWidgetAPI = () => {
  const [chatInitiated, setChatInitiated] = useState(false);
  const sessionData = getSessionData();
  const isDecagonWidget =
    !!sessionData && (sessionData.roles.includes("parent") || sessionData.roles.includes("teacher"));
  const decagonAuthTokenMutation = useDecagonAuthTokenMutation();
  const decagonIsAuthenticated = useRef<Date>();

  // we call the mutation directly to avoid adding the mutation to the dependency array
  const decagonAuthMutateCall = decagonAuthTokenMutation.mutate;

  const setDecagonUserIdAuth = useCallback(
    (userId: string) => {
      if (
        decagonIsAuthenticated.current &&
        decagonIsAuthenticated.current.getTime() > subHours(new Date(), 2).getTime()
      ) {
        return;
      }

      decagonAuthMutateCall(
        {},
        {
          onSuccess: ({ body }) => {
            callDecagonMethod(() => {
              windowWithDecagon.duet?.setUserId(userId);
              windowWithDecagon.duet?.setUserIdAuth(body.token, body.epoch);
              decagonIsAuthenticated.current = new Date();
            });
          },
          onError: () => {
            metrics.increment("marketplace.decagon.auth.failure");
          },
        },
      );
    },
    [decagonAuthMutateCall],
  );

  const setWidgetUserData = useCallback(
    ({ userId, userType, email, fullname, caller }: WidgetUserData) => {
      if (!email) {
        logClient.logMessage("WARN", `Setting widget user data without email: ${email}.  Set by ${caller}`);
      }

      setDecagonUserIdAuth(userId);

      const apiHost = getDecagonApiHost();
      const toolBranch = getDecagonToolBranch();
      const testMode = !isProduction || IS_TEST_SERVER;
      callDecagonMethod(() =>
        windowWithDecagon.duet?.setMetadata({
          email,
          user_type: userType === "teacher" ? "tutor" : userType,
          ...(toolBranch && { _decagon_branch_name: toolBranch }),
          isProduction,
          testMode,
          ...(apiHost && { api_host: apiHost }),
        }),
      );

      ZendeskAPI("webWidget", "updateSettings", {
        webWidget: {
          contactForm: {
            ticketForms: [{ id: ticketIdByUserType[userType] }],
            fields: [
              { id: "email", prefill: { "*": email || "" } },
              { id: "name", prefill: { "*": fullname || "" } },
            ],
          },
        },
      });
    },
    [setDecagonUserIdAuth],
  );

  const setDecagonEventListener = useCallback(() => {
    callDecagonMethod(() =>
      windowWithDecagon.duet?.setEventListener((event) => {
        if (event.eventName === "DUET_INITIALIZE") {
          setChatInitiated(true);
        }
      }),
    );
  }, []);

  // Handle Decagon widget initialization when chat is initiated
  useWatch([chatInitiated, sessionData, isDecagonWidget, setDecagonUserIdAuth], () => {
    if (!chatInitiated || !sessionData) return;
    if (!isDecagonWidget) return;

    if (sessionData.user_details.email) {
      setDecagonUserIdAuth(sessionData.user_details.uid);
    }
  });

  const openWidget = useCallback(
    ({ email: _email, name: _name }: ZendeskWidgetFields) => {
      if (isDecagonWidget) {
        callDecagonMethod(() => windowWithDecagon.duet?.open());
      } else {
        ZendeskAPI("webWidget", "updateSettings", {
          webWidget: {
            contactForm: {
              fields: [
                { id: "email", prefill: { "*": _email || "" } },
                { id: "name", prefill: { "*": _name || "" } },
              ],
            },
          },
        });
        ZendeskAPI("webWidget", "open");
      }
    },
    [isDecagonWidget],
  );

  const showWidget = useCallback(() => {
    if (isDecagonWidget) {
      ZendeskAPI("webWidget", "hide");
      callDecagonMethod(() => windowWithDecagon.duet?.unhide());
    } else {
      callDecagonMethod(() => windowWithDecagon.duet?.hide());
      ZendeskAPI("webWidget", "show");
    }
  }, [isDecagonWidget]);

  const hideWidget = () => {
    callDecagonMethod(() => windowWithDecagon.duet?.hide());
    ZendeskAPI("webWidget", "hide");
  };

  return useMemo(
    () => ({ isDecagonWidget, setWidgetUserData, openWidget, showWidget, hideWidget, setDecagonEventListener }),
    [isDecagonWidget, openWidget, setDecagonEventListener, showWidget, setWidgetUserData],
  );
};

export default useHelperWidgetAPI;
